import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  useCustomer,
  useCustomerContacts,
  useCustomerAddresses,
  useCustomerPortalInvites,
  useMintCustomerPortalInvite,
  useUpdateCustomer,
} from '@/data/customers';
import { useLoads } from '@/data/loads';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/input';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';
import { TableScroll } from '@/components/ui/table-scroll';
import { formatDateTime, formatMoney } from '@/lib/format';
import type {
  Customer,
  CustomerContact,
  CustomerAddress,
  Load,
} from '@breaker/shared-types';
import type { UpdateCustomerBody } from '@breaker/shared-schemas';

/** Field names as a person would say them — mirrors the API's copy. */
const BILLING_FIELD_LABEL: Record<string, string> = {
  billingAddressLine1: 'a billing address',
  billingPostcode: 'a postcode',
  billingEmail: 'a billing email',
  vatNumber: 'a VAT number',
};

export function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/authed/customers/$customerId' });
  const { fleet, entitlements } = useActiveFleet();
  const fleetId = fleet?.id ?? null;

  const customerQuery = useCustomer(fleetId, customerId);
  const contactsQuery = useCustomerContacts(fleetId, customerId);
  const addressesQuery = useCustomerAddresses(fleetId, customerId);
  const historyQuery = useLoads(fleetId, { customerId });

  const [editing, setEditing] = useState(false);

  if (customerQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <div className="px-8 py-10 max-w-[1100px]">
        <BackLink />
        <EmptyState
          title="Could not load customer"
          description={(customerQuery.error as Error)?.message ?? 'Server error.'}
        />
      </div>
    );
  }

  const customer = customerQuery.data;

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <BackLink />
      <header className="mb-6 mt-2 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
              {customer.name}
            </h1>
            {/* Same predicate as the issue trigger (BR110), read from the
                shared SQL function — so this can never say "fine" over a
                record the database will refuse to invoice. */}
            {(customer.billingMissingFields ?? []).length > 0 ? (
              <Badge tone="active">Billing incomplete</Badge>
            ) : null}
          </div>
          <p className="text-sm text-breaker-mute mt-1">
            {customer.accountRef ? `Account ${customer.accountRef}` : 'No account ref'}
            {customer.isArchived ? ' \u00B7 Archived' : ''}
          </p>
          {(customer.billingMissingFields ?? []).length > 0 ? (
            <p className="text-sm text-breaker-ink-2 mt-2">
              An invoice can’t be issued for this customer until you add{' '}
              {(customer.billingMissingFields ?? [])
                .map((f) => BILLING_FIELD_LABEL[f] ?? f)
                .join(' and ')}
              . Invoices freeze these details, so they can’t be corrected later.
            </p>
          ) : null}
        </div>
        {!editing ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : null}
      </header>

      {editing ? (
        <CustomerEditCard
          fleetId={fleetId}
          customer={customer}
          onDone={() => setEditing(false)}
        />
      ) : (
        <CustomerSummaryCard customer={customer} />
      )}

      {entitlements.customerPortal ? (
        <PortalInviteCard fleetId={fleetId} customer={customer} />
      ) : null}

      <ContactsCard
        loading={contactsQuery.isLoading}
        contacts={contactsQuery.data ?? []}
      />

      <AddressesCard
        loading={addressesQuery.isLoading}
        addresses={addressesQuery.data ?? []}
      />

      <HistoryCard
        loading={historyQuery.isLoading}
        loads={historyQuery.data ?? []}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/customers"
      className="text-sm text-breaker-mute hover:text-breaker-identity transition-colors"
    >
      {'\u2190'} Customers
    </Link>
  );
}

function CustomerSummaryCard({ customer }: { customer: Customer }) {
  return (
    <div className="surface rounded-lg p-5 mb-6">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Row label="Company number" value={customer.companyNumber} />
        <Row label="Account ref" value={customer.accountRef} />
        <Row label="Email" value={customer.primaryEmail} />
        <Row label="Phone" value={customer.primaryPhoneE164} />
        <Row label="Payment terms" value={`${customer.defaultPaymentTermsDays} days`} />
        <Row
          label="Credit limit"
          value={
            customer.creditLimitPence === null
              ? null
              : formatMoney(customer.creditLimitPence)
          }
        />
      </dl>
      {customer.notes ? (
        <div className="mt-4 pt-4 border-t border-breaker-line">
          <p className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 mb-1">
            Notes
          </p>
          <p className="text-sm text-breaker-ink whitespace-pre-wrap">{customer.notes}</p>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2">
        {label}
      </dt>
      <dd className="text-breaker-ink mt-0.5">
        {value ? value : <span className="text-breaker-mute">{'\u2014'}</span>}
      </dd>
    </div>
  );
}

function CustomerEditCard({
  fleetId,
  customer,
  onDone,
}: {
  fleetId: string | null;
  customer: Customer;
  onDone: () => void;
}) {
  const update = useUpdateCustomer(fleetId);
  const [name, setName] = useState(customer.name);
  const [companyNumber, setCompanyNumber] = useState(customer.companyNumber ?? '');
  const [accountRef, setAccountRef] = useState(customer.accountRef ?? '');
  const [email, setEmail] = useState(customer.primaryEmail ?? '');
  const [phone, setPhone] = useState(customer.primaryPhoneE164 ?? '');
  const [terms, setTerms] = useState(String(customer.defaultPaymentTermsDays));
  const [creditLimit, setCreditLimit] = useState(penceToInput(customer.creditLimitPence));
  const [notes, setNotes] = useState(customer.notes ?? '');
  const [archived, setArchived] = useState(customer.isArchived);
  // The billing block an invoice FREEZES at issue. This form is where the
  // BR110 refusal's "Add these to …" link lands, so the fields it names have
  // to be editable here — a fix route that arrives somewhere you still cannot
  // fix it is worse than no link at all.
  const [vatNumber, setVatNumber] = useState(customer.vatNumber ?? '');
  const [billingEmail, setBillingEmail] = useState(customer.billingEmail ?? '');
  const [billLine1, setBillLine1] = useState(customer.billingAddressLine1 ?? '');
  const [billLine2, setBillLine2] = useState(customer.billingAddressLine2 ?? '');
  const [billCity, setBillCity] = useState(customer.billingCity ?? '');
  const [billPostcode, setBillPostcode] = useState(customer.billingPostcode ?? '');
  const [billCountry, setBillCountry] = useState(customer.billingCountry ?? 'GB');
  const [error, setError] = useState<string | null>(null);

  function onSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return Promise.resolve();
    }
    setError(null);
    const termsNum = Number(terms);
    const body: UpdateCustomerBody = {
      name: trimmedName,
      companyNumber: blankToNull(companyNumber),
      accountRef: blankToNull(accountRef),
      primaryEmail: blankToNull(email),
      primaryPhoneE164: blankToNull(phone),
      defaultPaymentTermsDays: Number.isFinite(termsNum)
        ? Math.max(0, Math.min(365, Math.round(termsNum)))
        : 30,
      creditLimitPence: inputToPence(creditLimit),
      notes: blankToNull(notes),
      isArchived: archived,
      vatNumber: blankToNull(vatNumber),
      billingEmail: blankToNull(billingEmail),
      billingAddressLine1: blankToNull(billLine1),
      billingAddressLine2: blankToNull(billLine2),
      billingCity: blankToNull(billCity),
      billingPostcode: blankToNull(billPostcode),
      // Sent as typed; the server upper-cases it (one definition of the rule).
      billingCountry: blankToNull(billCountry),
    };
    return update
      .mutateAsync({ customerId: customer.id, body })
      .then(() => onDone())
      .catch((e: unknown) => {
        setError((e as Error)?.message ?? 'Could not save.');
      });
  }

  return (
    <div className="surface rounded-lg p-5 mb-6">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" htmlFor="cName">
          <Input id="cName" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Account ref" htmlFor="cAccount">
          <Input
            id="cAccount"
            value={accountRef}
            onChange={(e) => setAccountRef(e.target.value)}
          />
        </Field>
        <Field label="Company number" htmlFor="cCompany">
          <Input
            id="cCompany"
            value={companyNumber}
            onChange={(e) => setCompanyNumber(e.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor="cEmail">
          <Input
            id="cEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone (E.164)" htmlFor="cPhone">
          <Input
            id="cPhone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+447700900123"
          />
        </Field>
        <Field label="Payment terms (days)" htmlFor="cTerms">
          <Input
            id="cTerms"
            type="number"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
        </Field>
        <Field label="Credit limit (GBP)" htmlFor="cCredit">
          <Input
            id="cCredit"
            type="number"
            step="0.01"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-5 border-t border-breaker-line-2 pt-4">
        <h3 className="text-sm font-semibold text-breaker-ink">
          Billing details for invoices
        </h3>
        <p className="text-xs text-breaker-mute mt-1 mb-3">
          Copied onto their invoices when you issue one. An issued invoice
          can’t be edited afterwards, so these need to be right before the
          first job is billed.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="VAT number" htmlFor="cVat">
            <Input id="cVat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} placeholder="GB123456789" />
          </Field>
          <Field label="Billing email" htmlFor="cBillEmail">
            <Input id="cBillEmail" type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} />
          </Field>
          <Field label="Billing address" htmlFor="cBillLine1">
            <Input id="cBillLine1" value={billLine1} onChange={(e) => setBillLine1(e.target.value)} />
          </Field>
          <Field label="Address line 2" htmlFor="cBillLine2">
            <Input id="cBillLine2" value={billLine2} onChange={(e) => setBillLine2(e.target.value)} />
          </Field>
          <Field label="Town or city" htmlFor="cBillCity">
            <Input id="cBillCity" value={billCity} onChange={(e) => setBillCity(e.target.value)} />
          </Field>
          <Field label="Postcode" htmlFor="cBillPostcode">
            <Input id="cBillPostcode" value={billPostcode} onChange={(e) => setBillPostcode(e.target.value)} />
          </Field>
          <Field label="Country" htmlFor="cBillCountry">
            <Input id="cBillCountry" value={billCountry} onChange={(e) => setBillCountry(e.target.value)} maxLength={2} placeholder="GB" />
          </Field>
        </div>
      </div>

      <div className="mt-4">
        <Field label="Notes" htmlFor="cNotes">
          <Textarea
            id="cNotes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm text-breaker-ink">
        <input
          type="checkbox"
          checked={archived}
          onChange={(e) => setArchived(e.target.checked)}
        />
        Archived
      </label>

      {error ? <p className="text-xs text-breaker-red mt-3">{error}</p> : null}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onDone} disabled={update.isPending}>
          Cancel
        </Button>
        <Button size="sm" action={onSave} loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

function ContactsCard({
  loading,
  contacts,
}: {
  loading: boolean;
  contacts: CustomerContact[];
}) {
  return (
    <Section title="Contacts">
      {loading ? (
        <CardSpinner />
      ) : contacts.length === 0 ? (
        <p className="text-sm text-breaker-mute px-1 py-2">No contacts yet.</p>
      ) : (
        <ul className="divide-y divide-breaker-line">
          {contacts.map((c) => (
            <li key={c.id} className="py-2.5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-breaker-ink">
                  {c.fullName}
                  {c.isPrimary ? (
                    <span className="ml-2 text-2xs uppercase tracking-wider text-breaker-identity">
                      Primary
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-breaker-mute">{c.role ?? 'Contact'}</p>
              </div>
              <div className="text-right text-xs text-breaker-ink-2">
                {c.email ? <p>{c.email}</p> : null}
                {c.phoneE164 ? <p>{c.phoneE164}</p> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function AddressesCard({
  loading,
  addresses,
}: {
  loading: boolean;
  addresses: CustomerAddress[];
}) {
  return (
    <Section title="Addresses">
      {loading ? (
        <CardSpinner />
      ) : addresses.length === 0 ? (
        <p className="text-sm text-breaker-mute px-1 py-2">No saved addresses yet.</p>
      ) : (
        <ul className="divide-y divide-breaker-line">
          {addresses.map((a) => (
            <li key={a.id} className="py-2.5">
              <p className="text-sm font-medium text-breaker-ink">{a.label ?? a.line1}</p>
              <p className="text-xs text-breaker-mute">
                {[a.line1, a.line2, a.city, a.postcode].filter(Boolean).join(', ')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function HistoryCard({
  loading,
  loads,
}: {
  loading: boolean;
  loads: Load[];
}) {
  return (
    <Section title="Load history">
      {loading ? (
        <CardSpinner />
      ) : loads.length === 0 ? (
        <p className="text-sm text-breaker-mute px-1 py-2">
          No loads for this customer yet.
        </p>
      ) : (
        <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-breaker-line">
            <tr className="text-left">
              <HistoryTh>Reference</HistoryTh>
              <HistoryTh>Status</HistoryTh>
              <HistoryTh>Created</HistoryTh>
              <HistoryTh className="text-right">Total</HistoryTh>
            </tr>
          </thead>
          <tbody>
            {loads.map((d) => (
              <tr
                key={d.id}
                className="border-b last:border-b-0 border-breaker-line hover:bg-breaker-ground-2 transition-colors"
              >
                <td className="px-1 py-2.5">
                  <Link
                    to="/loads/$loadId"
                    params={{ loadId: d.id }}
                    className="font-medium text-breaker-ink hover:text-breaker-identity transition-colors"
                  >
                    {d.reference}
                  </Link>
                </td>
                <td className="px-1 py-2.5">
                  <StatusBadge status={d.status} />
                </td>
                <td className="px-1 py-2.5 text-breaker-ink-2">
                  {formatDateTime(d.createdAt)}
                </td>
                <td className="px-1 py-2.5 text-right text-breaker-ink-2">
                  {formatMoney(d.totalPence)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
    </Section>
  );
}

// ============================================================
// Portal invite (U6/U8): state row + "Invite to portal".
// ============================================================

function PortalInviteCard({
  fleetId,
  customer,
}: {
  fleetId: string | null;
  customer: Customer;
}) {
  const toast = useToast();
  const stateQuery = useCustomerPortalInvites(fleetId, customer.id);
  const mint = useMintCustomerPortalInvite(fleetId);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const s = stateQuery.data;

  function sendInvite() {
    const to = email.trim() || customer.billingEmail || '';
    if (!to) {
      toast.error('Enter an email address to invite.');
      return Promise.resolve();
    }
    return mint
      .mutateAsync({ customerId: customer.id, email: to })
      .then(() => {
        toast.success(`Portal invite sent to ${to}`);
        setEmail('');
        setInviting(false);
      })
      .catch((err) =>
        toast.error(err instanceof Error ? err.message : 'Invite failed'),
      );
  }

  return (
    <Section title="Customer portal">
      {stateQuery.isLoading || !s ? (
        <CardSpinner />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {s.state === 'accepted' ? (
              <Badge tone="success">Portal active</Badge>
            ) : s.state === 'pending' ? (
              <Badge tone="info">Invite pending</Badge>
            ) : (
              <Badge tone="neutral">Not invited</Badge>
            )}
            <p className="text-xs text-breaker-mute">
              {s.state === 'accepted'
                ? 'This customer signs in to the portal.'
                : s.state === 'pending' && s.pendingExpiresAt
                  ? `Invite expires ${formatDateTime(s.pendingExpiresAt)}.`
                  : 'No portal access yet — send an invite.'}
            </p>
          </div>

          {s.state !== 'accepted' &&
            (inviting ? (
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={customer.billingEmail ?? 'name@company.test'}
                  className="w-64"
                />
                <Button size="sm" loading={mint.isPending} action={sendInvite}>
                  Send
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInviting(false)}
                  disabled={mint.isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setInviting(true)}>
                {s.state === 'pending' ? 'Re-send invite' : 'Invite to portal'}
              </Button>
            ))}
        </div>
      )}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="surface rounded-lg p-5 mb-6">
      <h2 className="text-sm font-semibold text-breaker-ink mb-3">{title}</h2>
      {children}
    </section>
  );
}

function HistoryTh({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={
        'px-1 py-2 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 ' +
        (className ?? '')
      }
    >
      {children}
    </th>
  );
}

function CardSpinner() {
  return (
    <div className="flex justify-center py-6">
      <Spinner size="sm" />
    </div>
  );
}

function blankToNull(s: string): string | null {
  const t = s.trim();
  return t ? t : null;
}

function inputToPence(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function penceToInput(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return '';
  return (pence / 100).toFixed(2);
}
